import { useEffect, useRef, useState, createElement } from '@max/max';
import AnimatedView from '@max/mbc-animate-view';
import View from '@hfe/max-view';
import { Animated } from '@max/mbc-animated';
import { PanResponder } from '@max/mbc-panresponder';
import './index.scss';
import PanView from '@max/mbc-pan-view';
import { getFullScreenSize } from '@/utils/layout';
import { useMonitorEngineContext } from "@nibfe/mcp-monitor-sdk";
import { useLoopNumRef } from '@/hooks/useLoopNum';
import { extendArray } from '@/utils/common';
export default function InfiniteSteper(props) {
    const { data, renderItem, disabled = false, cardWidth } = props;
    const { toRateSize } = getFullScreenSize();
    // const CARD_WIDTH = toRateSize(containerWidth / data.length)
    const CARD_WIDTH = toRateSize(cardWidth);
    const THRESHOLD = toRateSize(50);
    const ctx = useMonitorEngineContext();
    const [scrolllingCards, setScrolllingCards] = useState([]);
    const position = useRef(new Animated.Value(0)).current;
    const [tailFilledCardIndex, nextTail] = useLoopNumRef({
        size: data.length
    });
    const [headFilledCardIndex, nextHead] = useLoopNumRef({
        size: data.length,
        reverse: true
    });
    const [centerIndex, setCenterIndex] = useState(Math.floor(data.length / 2));
    const isScrolling = useRef(false);
    const indexNum = useRef(0); // 为每个card指定唯一key，原逻辑中用index作为key会导致右滑结束后，将头部card移除时，key变化产生图片闪烁
    function slide(direction = 'left', num = 1) {
        if (isScrolling.current || disabled) {
            return;
        }
        isScrolling.current = true;
        // 准备数据
        setScrolllingCards(prev => {
            var _a;
            const midIndex = direction === 'left' ? 2 : 0; // 初始态index=1的要素为中间元素，向左滑动则+1，向右滑动则-1
            setCenterIndex(midIndex);
            (_a = props.onTopShow) === null || _a === void 0 ? void 0 : _a.call(props, prev[midIndex]);
            if (direction === 'left') {
                const insertItems = Array.from({
                    length: num
                }).map((_, index) => {
                    const inserIndex = (tailFilledCardIndex.current + index) % data.length;
                    const target = Object.assign(Object.assign({}, data[inserIndex]), { indexNum: indexNum.current });
                    indexNum.current++;
                    return target;
                });
                nextTail(num);
                nextHead(-num);
                return [...prev, ...insertItems];
            }
            else {
                const insertItems = Array.from({
                    length: num
                }).map((_, index) => {
                    const inserIndex = (headFilledCardIndex.current + data.length - index) % data.length;
                    const target = Object.assign(Object.assign({}, data[inserIndex]), { indexNum: indexNum.current });
                    indexNum.current++;
                    return target;
                });
                nextHead(num);
                nextTail(-num);
                return [...insertItems.reverse(), ...prev];
            }
        });
        if (direction === 'right') {
            position.setValue(-CARD_WIDTH * num);
        }
        ctx._pageAPI.enableScroll();
        // 所有card向左/右移动num个card宽度
        Animated.timing(position, 
        // 当前位置
        {
            toValue: direction === 'left' ? -CARD_WIDTH * num : 0,
            // 目标位置
            easing: Animated.Easing.bezier(0.25, 0.1, 0.25, 1.0),
            useNativeDriver: true,
            duration: num === 1 ? 300 : num * 210
        }).start(({ finished }) => {
            if (!finished)
                return;
            isScrolling.current = false;
            position.setValue(0);
            setScrolllingCards(prev => {
                setCenterIndex(Math.floor(data.length / 2));
                if (direction === 'left') {
                    return prev.slice(num);
                }
                else {
                    return prev.slice(0, -num);
                }
            });
        });
    }
    // 定义手势
    const panResponder = PanResponder.create({
        onStartShouldSetPanResponder: () => false,
        // 是否响应触摸开始事件
        onStartShouldSetPanResponderCapture: () => {
            // 是否在捕获阶段响应触摸开始事件
            // 防止页面滚动与组件内的手势冲突
            ctx._pageAPI.disableScroll();
            return false;
        },
        onMoveShouldSetPanResponder: (_, gesture) => {
            // 是否响应移动事件
            // 只有当水平方向有位移时（gesture.dx !== 0）才响应手势
            const isHasScroll = gesture.dx !== 0;
            return isHasScroll;
        },
        onPanResponderRelease: (_, gesture) => {
            // 手指释放时的回调
            ctx._pageAPI.enableScroll();
            if (gesture.dx < -THRESHOLD) {
                slide('left');
            }
            else if (gesture.dx > THRESHOLD) {
                slide('right');
            }
        }
    });
    useEffect(() => {
        var _a;
        const expandData = extendArray(data);
        setScrolllingCards(() => {
            indexNum.current = 0;
            return expandData.map(item => {
                const dataItem = Object.assign(Object.assign({}, item), { indexNum: indexNum.current });
                indexNum.current++;
                return dataItem;
            });
        });
        // index为1的元素为中间元素
        const midIndex = 1;
        (_a = props.onTopShow) === null || _a === void 0 ? void 0 : _a.call(props, data[midIndex]);
    }, [data]);
    return <PanView panHandlers={panResponder.panHandlers} className="card-container" style={{
        width: `${3 * cardWidth}rpx` // 可视区只占3个card，左、中、右
    }}>
      {scrolllingCards.map((card, index) => {
        const inputRange = [
            // (-index - 1) * CARD_WIDTH,
            (-index - 0) * CARD_WIDTH,
            // <- center 
            (-index + 1) * CARD_WIDTH, (-index + 2) * CARD_WIDTH
        ];
        const mappedOpacity = position.interpolate({
            inputRange,
            outputRange: [0.9, 1, 0.9],
            extrapolate: 'clamp'
        });
        const mappedScale = position.interpolate({
            inputRange,
            outputRange: [0.8, 1, 0.8],
            extrapolate: 'clamp'
        });
        const mappedTranslateX = Animated.add(position, position.interpolate({
            inputRange,
            outputRange: [toRateSize(24), 0, -toRateSize(24)]
        }));
        function interpolateTo(outputRange) {
            return position.interpolate({
                inputRange,
                outputRange,
                extrapolate: 'clamp'
            });
        }
        return <AnimatedView key={card.indexNum} viewId={'view' + +card.indexNum} style={{
            height: '396rpx',
            position: 'relative',
            width: '538rpx',
            zIndex: data.length - Math.abs(centerIndex - index)
        }} animateStyles={{
            opacity: mappedOpacity,
            transform: [{
                    translateX: mappedTranslateX
                }, {
                    scale: mappedScale
                }]
        }}>
            <View className='card-content' onClick={() => {
            var _a;
            const midIndex = 1;
            (_a = props.onPress) === null || _a === void 0 ? void 0 : _a.call(props, card, index);
            if (index < midIndex) {
                slide('right', midIndex - index);
            }
            else if (index > midIndex) {
                slide('left', index - midIndex);
            }
        }}>
              <View className='flex-center'>
                {renderItem(card, card.indexNum, interpolateTo, centerIndex === index)}
              </View>
            </View>
          </AnimatedView>;
    })}
    </PanView>;
}
;
